import React from 'react';
import {
  auditLinkDefaultBody,
  auditLinkDraftBody,
  parseAuditLinkId,
  readStoredAuditLinkId,
  safeStorageRemove,
  stripAuditLinkToken,
  withAuditLinkToken,
  writeStoredAuditLinkId,
} from './investigationFeedHelpers.js';

const AUDIT_LINK_ID_RE = /^[A-Za-z0-9._:-]{1,160}$/;

export const useInvestigationAuditLink = ({
  noteDraft,
  setNoteDraft,
  scheduleNoteSave,
  row = null,
  runId = '',
  varianceActions = [],
}) => {
  const [activeAuditLinkId, setActiveAuditLinkId] = React.useState('');
  const actionById = React.useMemo(
    () => new Map((varianceActions || []).filter((action) => action?.id).map((action) => [String(action.id), action])),
    [varianceActions],
  );
  const storageKey = React.useMemo(
    () => `wip.billingRecon.investigationAuditLink.v1.${runId || 'local'}.${row?.bac || 'unknown'}`,
    [row?.bac, runId],
  );
  const canValidateAuditLinks = (varianceActions || []).length > 0;
  const isValidAuditLinkId = React.useCallback((id) => {
    const normalized = String(id || '').trim();
    if (!AUDIT_LINK_ID_RE.test(normalized)) return false;
    return !canValidateAuditLinks || actionById.has(normalized);
  }, [actionById, canValidateAuditLinks]);

  React.useEffect(() => {
    const parsed = parseAuditLinkId(noteDraft);
    if (!parsed || parsed === activeAuditLinkId) return;
    if (!isValidAuditLinkId(parsed)) {
      setActiveAuditLinkId('');
      safeStorageRemove(storageKey);
      return;
    }
    setActiveAuditLinkId(parsed);
    writeStoredAuditLinkId(storageKey, parsed);
  }, [activeAuditLinkId, isValidAuditLinkId, noteDraft, storageKey]);

  React.useEffect(() => {
    const stored = readStoredAuditLinkId(storageKey);
    if (stored && !isValidAuditLinkId(stored)) {
      safeStorageRemove(storageKey);
      setActiveAuditLinkId('');
      return;
    }
    setActiveAuditLinkId(stored);
    if (stored && !parseAuditLinkId(noteDraft) && !stripAuditLinkToken(noteDraft)) {
      const storedAction = actionById.get(stored);
      setNoteDraft(withAuditLinkToken(auditLinkDefaultBody(storedAction || { id: stored }), stored));
    }
    // row/storage changes are the only moments we want to auto-seed the composer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey, isValidAuditLinkId]);

  const activeAuditAction = activeAuditLinkId ? actionById.get(activeAuditLinkId) : null;
  const draftBody = auditLinkDraftBody(noteDraft);

  const updateDraftBody = React.useCallback((body) => {
    const next = withAuditLinkToken(body, activeAuditLinkId);
    setNoteDraft(next);
    scheduleNoteSave(body.trim() ? next : '');
  }, [activeAuditLinkId, scheduleNoteSave, setNoteDraft]);

  const setAuditLink = React.useCallback((actionId) => {
    if (!actionId) return;
    const id = String(actionId).trim();
    if (!isValidAuditLinkId(id)) {
      safeStorageRemove(storageKey);
      return;
    }
    const action = actionById.get(id);
    setActiveAuditLinkId(id);
    writeStoredAuditLinkId(storageKey, id);
    const currentBody = stripAuditLinkToken(noteDraft) ? noteDraft : auditLinkDefaultBody(action || { id });
    const next = withAuditLinkToken(currentBody, id);
    setNoteDraft(next);
    scheduleNoteSave(next);
  }, [actionById, isValidAuditLinkId, noteDraft, scheduleNoteSave, setNoteDraft, storageKey]);

  const clearAuditLink = React.useCallback(() => {
    setActiveAuditLinkId('');
    safeStorageRemove(storageKey);
    const next = stripAuditLinkToken(noteDraft);
    setNoteDraft(next);
    scheduleNoteSave(next);
  }, [noteDraft, scheduleNoteSave, setNoteDraft, storageKey]);

  return {
    actionById,
    activeAuditAction,
    activeAuditLinkId,
    clearAuditLink,
    draftBody,
    setAuditLink,
    updateDraftBody,
  };
};
